/* eslint-disable */
import { Component, computed, EventEmitter, inject, Injector, input, OnInit, Output } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatIconModule } from '@angular/material/icon';
import { Router } from '@angular/router';
import { TranslocoPipe } from '@jsverse/transloco';
import { GuidelinesHttpService } from 'guideline/domain';
import { NgxPermissionsService } from 'ngx-permissions';
import { LicenseService, MessagesService, UserService } from 'shared/domain';
import { NavShellService, NavShellState } from 'libs/shared/domain/src/lib/infrastructure/nav-shell.service';
import { ExpandService } from 'libs/shared/util-common/src/lib/expand.service';
import { GuidelinePdfExportService } from '../guideline-pdf-export.service';
import { ConfirmationDialogNewComponent, DialogService } from 'shared/ui-common';
import { GuidelineService } from 'libs/shared/domain/src/lib/infrastructure/guideline-page-domain/guideline.service';
import { RolePermissions } from 'libs/shared/domain/src/lib/infrastructure/PermissionResolver';
import { ExportDialogComponent, ExportDialogData } from './export-dialog/export-dialog.component';
import { ExpandCollapseToggleComponent } from './expand-collapse-toggle/expand-collapse-toggle.component';
import { AddZToUTCPipe } from 'libs/shared/util-common/src/lib/pipes/add-z-to-utc.pipe';
import { SharedUtilCommonModule } from 'libs/shared/util-common/src/lib/util-common.module';
import { IconDirective } from 'libs/shared/ui-common/src/lib/components/icon/icon.directive';
import { TooltipDirective } from 'libs/shared/ui-common/src/lib/components/tooltip/tooltip.directive';
import { ButtonDirective } from 'libs/shared/ui-common/src/lib/components/button/button.directive';
import { TranslocoDatePipe } from '@jsverse/transloco-locale';
import { ImportGuidelineDialogComponent } from 'shared/guideline-import-update-settings';
import { isTruthy, OrganisationSettingsService } from 'shared/util-common';
import { GuidelineImport, GuidelineImportErrorCode, Organisation } from '@treetop/shared/model';
import { take } from 'rxjs';
import { CommentsFooterButtonComponent } from '../../guideline-edit/guideline-edit/guideline-comments/comments-footer-button/comments-footer-button.component';

@Component({
  selector: 'smartbook-footer-component',
  standalone: true,
  imports: [
    FormsModule,
    ExpandCollapseToggleComponent,
    AddZToUTCPipe,
    TranslocoPipe,
    SharedUtilCommonModule,
    MatIconModule,
    IconDirective,
    TooltipDirective,
    ButtonDirective,
    TranslocoDatePipe,
    CommentsFooterButtonComponent,
  ],
  templateUrl: './smartbook-footer.component.html',
  styleUrls: ['./smartbook-footer.component.scss'],
})
export class SmartbookFooterComponent implements OnInit {
  private router = inject(Router);
  private expandService = inject(ExpandService);
  public licenseService = inject(LicenseService);
  private navShellService = inject(NavShellService);
  private pdfExportService = inject(GuidelinePdfExportService, { optional: true });
  private dialogService = inject(DialogService);
  private permissionsService = inject(NgxPermissionsService, { optional: true });
  private guidelineService = inject(GuidelineService);
  private injector = inject(Injector);
  private guidelinesHttpService = inject(GuidelinesHttpService);
  private messagesService = inject(MessagesService);
  private organisationSettingsService = inject(OrganisationSettingsService);

  protected guidelineData = this.guidelineService.guidelineData;

  public allowExportAsPdf = input(true);
  public isPublicPage = input<boolean>(false);
  public version = input<string | undefined>();
  public publishedAt = input<Date | undefined>();

  @Output() public mobileMenuClicked = new EventEmitter<void>();

  protected isMenuOpen = computed(() => this.navShellService.sideNavState() === NavShellState.open);
  protected canExportPdf = computed(() => {
    if (this.isPublicPage() || !this.allowExportAsPdf()) {
      return false;
    }

    const hasPermission = this.permissionsService?.getPermission(RolePermissions.exportPdf) != null;

    return this.licenseService.licensedFeatures()?.pdfExport && hasPermission;
  });
  protected canExportHtml = computed(
    () => this.canExportPdf() && this.organisationSettingsService.settings()?.exportSettings?.htmlExportEnabled === true,
  );

  protected importTargetOrganisations = computed(() => {
    const userService = this.injector.get(UserService);
    const user = userService.user();
    const sourceOrganisationId = this.guidelineData()?.organisationId;
    const writableOrganisationIds = user?.organisationIdsWithEditorPermission ?? [];

    return userService
      .organisationsWithEditorPermission()
      .filter((organisation) => !!organisation.id)
      .filter((organisation) => writableOrganisationIds.includes(organisation.id!))
      .filter((organisation) => organisation.id !== sourceOrganisationId);
  });

  protected selectedImportTargetOrganisationId = computed(() => {
    const currentOrganisationId = this.injector.get(UserService).user()?.currentOrganisation?.id;
    const targetOrganisations = this.importTargetOrganisations();

    if (currentOrganisationId && targetOrganisations.some((organisation) => organisation.id === currentOrganisationId)) {
      return currentOrganisationId;
    }

    return targetOrganisations.length === 1 ? targetOrganisations[0].id : undefined;
  });
  protected canImportGuideline = computed(() => {
    const guideline = this.guidelineData();

    if (this.isPublicPage()) {
      return false;
    }

    return !!guideline?.uniqueId && !!guideline.organisationId && this.importTargetOrganisations().length > 0;
  });

  public ngOnInit() {
    this.adjustMatDrawerMargin();
  }

  protected handleClose() {
    void this.router.navigateByUrl('/smartbook');
  }

  protected adjustMatDrawerMargin() {
    const matDrawer = document.querySelector<HTMLElement>('.mat-drawer');
    if (matDrawer) {
      matDrawer.style.marginBottom = '60px';
    }
  }

  protected handleMobileMenuClick() {
    if (this.isMenuOpen()) {
      this.navShellService.close();
    } else {
      this.navShellService.open();
    }
  }

  protected handleDefaultViewClicked() {
    this.expandService.reset();
  }

  protected handleImportClicked() {
    const guideline = this.guidelineData();
    const targetOrganisations = this.importTargetOrganisations();
    if (!guideline?.uniqueId || targetOrganisations.length === 0) {
      return;
    }

    this.openImportSettingsDialog(guideline.uniqueId, guideline.title ?? '');
  }

  private validateAndImportGuideline(guidelineImport: GuidelineImport) {
    this.guidelinesHttpService
      .validateImport(guidelineImport)
      .pipe(take(1))
      .subscribe((result) => {
        switch (result.validationResult.errorCode) {
          case GuidelineImportErrorCode.None:
            this.importGuideline(guidelineImport);
            break;
          case GuidelineImportErrorCode.AlreadyImported:
          case GuidelineImportErrorCode.SameOrganisation:
            this.dialogService.open(ConfirmationDialogNewComponent, {
              title: 'ImportGuidelineDialog.Error.AlreadyImported.Title',
              width: 536,
              data: {
                text: 'ImportGuidelineDialog.Error.AlreadyImported.Message',
              },
            });
            break;
          case GuidelineImportErrorCode.NotLicensedForHub:
            this.dialogService.open(ConfirmationDialogNewComponent, {
              title: 'ImportGuidelineDialog.Error.NoHubLicense.Title',
              data: {
                text: 'ImportGuidelineDialog.Error.NoHubLicense.Message',
              },
            });
            break;
          case GuidelineImportErrorCode.PaidGuidelineLimitExceeded:
            this.dialogService.open(ConfirmationDialogNewComponent, {
              title: 'ImportGuidelineDialog.Error.PaidGuidelineLimitExceeded.Title',
              data: {
                text: 'ImportGuidelineDialog.Error.PaidGuidelineLimitExceeded.Message',
              },
            });
            break;
          default:
            this.dialogService.open(ConfirmationDialogNewComponent, {
              title: 'ImportGuidelineDialog.Error.General.Title',
              data: {
                text: 'ImportGuidelineDialog.Error.General.Message',
              },
            });
            break;
        }
      });
  }

  private openImportSettingsDialog(guidelineUniqueId: string, guidelineName: string) {
    this.dialogService
      .open<
        {
          uniqueId: string;
          targetOrganisations: Organisation[];
          selectedTargetOrganisationId?: string;
        },
        GuidelineImport
      >(ImportGuidelineDialogComponent, {
        title: 'ImportGuidelineDialog.Title',
        titleParams: { guidelineName },
        width: 536,
        data: {
          uniqueId: guidelineUniqueId,
          targetOrganisations: this.importTargetOrganisations(),
          selectedTargetOrganisationId: this.selectedImportTargetOrganisationId(),
        },
      })
      .afterClosed()
      .pipe(isTruthy())
      .subscribe((importGuidelineData) => {
        this.validateAndImportGuideline(importGuidelineData);
      });
  }

  private importGuideline(importGuidelineData: GuidelineImport) {
    this.guidelinesHttpService
      .import(importGuidelineData)
      .pipe(take(1))
      .subscribe({
        next: (result) => {
          void this.router.navigate([`manage/guideline/${result.uniqueId}`]);
        },
        error: (error) => {
          this.messagesService.showError('ManagePage.Error.ImportGuideline', error);
        },
      });
  }

  protected handleExportPdfClicked() {
    if (!this.pdfExportService) {
      console.error('PDF export service not available');
      return;
    }

    const guidelineUniqueId = this.guidelineData()?.uniqueId;
    if (!guidelineUniqueId) {
      console.error('Cannot export PDF: guideline unique ID not found');
      return;
    }

    const version = this.version() || null;

    const dialogData: ExportDialogData = {
      guidelineUniqueId,
      guidelineTitle: this.guidelineService.guidelineData()?.title,
      hasWorkflow: this.hasWorkflow(),
      format: 'pdf',
      export: () => {
        if (!this.pdfExportService) {
          return undefined;
        }

        return this.pdfExportService.exportGuidelineToPdf(guidelineUniqueId, version);
      },
    };

    this.dialogService.open(ExportDialogComponent, {
      title: 'PdfExportDialog.Title',
      width: 500,
      disableClose: true,
      data: dialogData,
    });
  }

  protected handleExportHtmlClicked() {
    if (!this.pdfExportService) {
      console.error('PDF export service not available');
      return;
    }

    const guidelineUniqueId = this.guidelineData()?.uniqueId;
    if (!guidelineUniqueId) {
      console.error('Cannot export HTML: guideline unique ID not found');
      return;
    }

    const version = this.version() || null;

    const dialogData: ExportDialogData = {
      guidelineUniqueId,
      guidelineTitle: this.guidelineService.guidelineData()?.title,
      hasWorkflow: this.hasWorkflow(),
      format: 'html',
      export: () => {
        if (!this.pdfExportService) {
          return undefined;
        }

        return this.pdfExportService.exportGuidelineToHtml(guidelineUniqueId, version);
      },
    };

    this.dialogService.open(ExportDialogComponent, {
      title: 'HtmlExportDialog.Title',
      width: 500,
      disableClose: true,
      data: dialogData,
    });
  }

  private hasWorkflow(): boolean {
    return !!Object.values(this.guidelineService.map() ?? {}).find((widget) => widget().widgetType === 'workflow');
  }
}
